import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { COUNTRIES } from "../lib/countries";
import { toast } from "sonner";
import { UserPlus, Eye, EyeOff, ArrowRight, ArrowLeft, ShieldCheck, Smartphone, FileText } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

function makeCaptcha() {
    return { a: 1 + Math.floor(Math.random() * 9), b: 1 + Math.floor(Math.random() * 9) };
}

function TermsModal({ open, onClose }) {
    const { t } = useTranslation();
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" data-testid="terms-modal" onClick={onClose}>
            <div className="bg-white max-w-2xl w-full rounded-3xl p-7 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-[var(--medx-orange)]" />
                    <h2 className="font-display text-2xl font-semibold">{t("auth.signup.terms.title")}</h2>
                </div>
                <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
                    {["p1","p2","p3","p4","p5","p6","p7","p8","p9","p10"].map((k) => (
                        <p key={k} dangerouslySetInnerHTML={{ __html: t(`auth.signup.terms.${k}`) }} />
                    ))}
                </div>
                <button onClick={onClose} className="btn-primary mt-6 w-full" data-testid="terms-close">{t("auth.signup.terms.understand")}</button>
            </div>
        </div>
    );
}

export default function Signup() {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const navigate = useNavigate();
    const { setUser } = useAuth();

    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        age: "",
        country: "South Africa",
        country_code: "+27",
        mobile: "",
        email: "",
        password: "",
        confirm: "",
    });
    const [show, setShow] = useState(false);
    const [terms, setTerms] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [captcha, setCaptcha] = useState(makeCaptcha());
    const [captchaAnswer, setCaptchaAnswer] = useState("");
    const [otp, setOtp] = useState("");
    const [emailSent, setEmailSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleCountryChange = (countryName) => {
        const c = COUNTRIES.find((x) => x.name === countryName);
        setForm((f) => ({ ...f, country: countryName, country_code: c ? c.dial : "+1" }));
    };

    const submitStep1 = async (e) => {
        e.preventDefault();
        if (!form.first_name || !form.last_name || !form.age || !form.mobile || !form.email || !form.password) {
            toast.error(t("auth.signup.errors.completeFields"));
            return;
        }
        if (form.password.length < 6) { toast.error(t("auth.signup.errors.passwordMin")); return; }
        if (form.password !== form.confirm) { toast.error(t("auth.signup.errors.passwordMatch")); return; }
        if (!terms) { toast.error(t("auth.signup.errors.termsRequired")); return; }
        if (Number(captchaAnswer) !== captcha.a + captcha.b) { toast.error(t("auth.signup.errors.captchaWrong")); setCaptcha(makeCaptcha()); setCaptchaAnswer(""); return; }

        setLoading(true);
        try {
            const { data } = await api.post("/auth/register-init", {
                first_name: form.first_name,
                last_name: form.last_name,
                age: Number(form.age),
                country: form.country,
                country_code: form.country_code,
                mobile: form.mobile,
                email: form.email,
                password: form.password,
                terms_accepted: terms,
                captcha_a: captcha.a,
                captcha_b: captcha.b,
                captcha_answer: Number(captchaAnswer),
            });
            setEmailSent(!!data.email_sent);
            toast.success(data.message || "Verification code sent.");
            setStep(2);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || t("auth.signup.errors.regFailed"));
        } finally {
            setLoading(false);
        }
    };

    const submitStep2 = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) { toast.error(t("auth.signup.errors.otpLength")); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/verify-otp", { email: form.email, code: otp });
            localStorage.setItem("medx_token", data.token);
            setUser(data.user);
            toast.success(t("auth.signup.welcome", { name: data.user.first_name || data.user.name.split(" ")[0] }));
            navigate("/dashboard");
			setUser(data.user);
			window.location.reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || t("auth.signup.errors.verifyFailed"));
        } finally {
            setLoading(false);
        }
    };

    const dialOptions = useMemo(() => COUNTRIES.map((c) => c.dial).filter((v, i, a) => a.indexOf(v) === i), []);

    return (
        <div className="bg-white min-h-screen" data-testid="signup-page">
            <Navbar />
            <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />
            <main className="pt-28 pb-20">
                <section className="max-w-xl mx-auto px-6">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3 text-center">{t("auth.signup.eyebrow")}</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-center mb-3">
                        {t("auth.signup.titlePre")}<span className="brand-gradient-text">{t("auth.signup.titleAccent")}</span>{t("auth.signup.titlePost")}
                    </h1>
                    <p className="text-center text-sm text-gray-500 mb-8">
                        {t("auth.signup.step", { step, label: step === 1 ? t("auth.signup.step1Label") : t("auth.signup.step2Label") })}
                    </p>

                    <div className="flex items-center gap-2 mb-8 max-w-xs mx-auto">
                        <div className={`flex-1 h-1 rounded-full ${step >= 1 ? "bg-[var(--medx-red)]" : "bg-gray-200"}`} />
                        <div className={`flex-1 h-1 rounded-full ${step >= 2 ? "bg-[var(--medx-orange)]" : "bg-gray-200"}`} />
                    </div>

                    <div className="card-soft p-7">
                        {step === 1 && (
                            <form onSubmit={submitStep1} className="space-y-4" data-testid="signup-step1">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.signup.firstName")}</label>
                                        <input required type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                                            className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                            data-testid="signup-first-name" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.signup.lastName")}</label>
                                        <input required type="text" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                                            className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                            data-testid="signup-last-name" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.signup.age")}</label>
                                        <select required value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                                            className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm bg-white"
                                            data-testid="signup-age">
                                            <option value="">{t("auth.signup.selectAge")}</option>
                                            {Array.from({ length: 100 }, (_, i) => i + 1).map((n) => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.signup.country")}</label>
                                        <select required value={form.country} onChange={(e) => handleCountryChange(e.target.value)}
                                            className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm bg-white"
                                            data-testid="signup-country">
                                            {COUNTRIES.map((c) => (
                                                <option key={c.code} value={c.name}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.signup.mobile")}</label>
                                    <div className="flex mt-1 gap-2">
                                        <select value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value })}
                                            className="px-3 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm bg-white shrink-0"
                                            data-testid="signup-dial-code">
                                            {dialOptions.sort().map((d) => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        <input required type="tel" placeholder="71 234 5678" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                                            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                            data-testid="signup-mobile" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.signup.email")}</label>
                                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="signup-email" />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.signup.password")}</label>
                                        <div className="relative mt-1">
                                            <input required type={show ? "text" : "password"} minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                                className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                                data-testid="signup-password" />
                                            <button type="button" onClick={() => setShow((v) => !v)} className="absolute inset-y-0 right-3 grid place-items-center text-gray-400">
                                                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.signup.confirm")}</label>
                                        <input required type={show ? "text" : "password"} minLength={6} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                            className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                            data-testid="signup-confirm" />
                                    </div>
                                </div>

                                <div className="bg-orange-50/50 border border-orange-100 rounded-xl p-4">
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500 flex items-center gap-2">
                                        <ShieldCheck className="w-4 h-4 text-[var(--medx-orange)]" /> {t("auth.signup.humanCheck")}
                                    </label>
                                    <div className="flex items-center gap-3 mt-2">
                                        <div className="font-mono text-lg text-gray-800 select-none">
                                            {captcha.a} <span className="text-[var(--medx-red)]">+</span> {captcha.b} =
                                        </div>
                                        <input required type="number" value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)}
                                            className="w-24 px-3 py-2 rounded-lg border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                            data-testid="signup-captcha-answer" />
                                        <button type="button" onClick={() => { setCaptcha(makeCaptcha()); setCaptchaAnswer(""); }} className="text-xs text-[var(--medx-orange)] underline">
                                            {t("auth.signup.captchaNew")}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <input id="terms" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
                                        className="mt-1 w-4 h-4 accent-[var(--medx-red)]" data-testid="signup-terms-check" />
                                    <label htmlFor="terms" className="text-sm text-gray-600">
                                        {t("auth.signup.terms")}{" "}
                                        <button type="button" onClick={() => setShowTerms(true)} className="text-[var(--medx-red)] font-semibold underline" data-testid="signup-terms-open">
                                            {t("auth.signup.termsLink")}
                                        </button>
                                        .
                                    </label>
                                </div>

                                <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="signup-submit-step1">
                                    {loading ? t("auth.signup.sendingCode") : (<>{t("auth.signup.submitStep1")} <ArrowRight className="w-4 h-4" /></>)}
                                </button>

                                <div className="text-center text-sm text-gray-500">
                                    {t("auth.signup.already")} <Link to="/login" className="text-[var(--medx-red)] font-semibold hover:underline">{t("auth.signup.loginLink")}</Link>
                                </div>
                            </form>
                        )}

                        {step === 2 && (
                            <form onSubmit={submitStep2} className="space-y-4 text-center" data-testid="signup-step2">
                                <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 grid place-items-center">
                                    <Smartphone className="w-7 h-7 text-[var(--medx-orange)]" />
                                </div>
                                <h2 className="font-display text-2xl font-semibold">
                                    {emailSent ? t("auth.signup.checkEmail") : t("auth.signup.almostThere")}
                                </h2>
                                <p className="text-sm text-gray-600">
                                    <Trans
                                        i18nKey={emailSent ? "auth.signup.codeSentEmail" : "auth.signup.codeSentSMS"}
                                        values={{ email: form.email, phone: `${form.country_code} ${form.mobile}` }}
                                        components={{ strong: <strong /> }}
                                    />
                                </p>
                                <input
                                    type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                                    placeholder="••••••"
                                    className="w-full px-4 py-4 rounded-xl border-2 border-gray-200 focus:border-[var(--medx-orange)] outline-none text-center text-2xl font-mono tracking-[0.6em]"
                                    data-testid="signup-otp"
                                />
                                <button type="submit" disabled={loading || otp.length !== 6} className="btn-primary w-full disabled:opacity-60" data-testid="signup-verify-otp">
                                    {loading ? t("auth.signup.verifying") : (<><UserPlus className="w-4 h-4 inline mr-2" /> {t("auth.signup.verify")}</>)}
                                </button>
                                <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-[var(--medx-red)] inline-flex items-center gap-2" data-testid="signup-back">
                                    <ArrowLeft className="w-3 h-3" /> {t("auth.signup.backToDetails")}
                                </button>
                            </form>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
}
